import { useEffect, useMemo, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { toDateTimeLocal } from '../../../../lib/formatDate';
import { focusValidationError } from '../../../../lib/validationFocus';
import { dispatchedDocumentsApi } from '../routingApi';
import {
  DISPATCH_MODE,
  DOCUMENT_MEDIUM,
  MEDIUM_LABEL,
  MODE_LABEL,
  type DispatchMode,
  type DispatchableDocumentOption,
  type CorrespondentKind,
  type DispatchedDocumentDetail,
  type DocumentMedium,
  type PersonNameParts,
  type RoutingUnitOption,
} from '../routingTypes';
import { useDocumentTypeField } from '../useDocumentTypeField';
import { fileAll } from '../utils/fileAttachments';
import { sampleDispatchedDocument } from '../utils/sampleRouting';
import { CorrespondentNameFields } from './CorrespondentNameFields';
import { CORRESPONDENT_KIND_OPTIONS, EMPTY_NAME, fileTooLarge, nameIncomplete } from '../routingForm';
import { RoutingFilesInput } from './RoutingFilesInput';

const ID_PREFIX = 'dispatched-document';

type Source = 'received' | 'originated';

const MEDIUM_OPTIONS = Object.values(DOCUMENT_MEDIUM).map((m) => ({ value: String(m), label: MEDIUM_LABEL[m] }));
const MODE_OPTIONS = Object.values(DISPATCH_MODE).map((m) => ({ value: String(m), label: MODE_LABEL[m] }));

/**
 * Record or correct an outgoing document.
 *
 * **Two sources, one question first.** Either it sends on a document the organization RECEIVED —
 * then it leaves from the unit holding that document, which the server reads, and closes its
 * route — or a unit WROTE it, and the reader names that unit. The two pickers are drawn always
 * and only the one the answer calls for is enabled, so nothing appears out of nowhere.
 *
 * **The source is FIXED on an edit** — the server refuses a change, and the question is locked
 * with the reason. Delete and record it again.
 *
 * **The addressee is a unit OR an outside office**, the received document's question asked the
 * other way. A unit is picked, and the dispatch lands in that unit's Inbox, in transit until it is
 * taken in there; only an outside office carries a named person. **Once the Inbox has received it
 * the addressee is fixed** — the server refuses a change, and the fields say why.
 *
 * **A received document may be with SEVERAL units** (10 Oct 2026). With one, it leaves from that
 * unit and the server reads it; with several, the From unit picker opens on THOSE units and the
 * reader chooses which copy goes out — the others stay where they are.
 *
 * `initialReceivedId` SEEDS the received document when the form is opened from that document's
 * own profile; it does not lock it.
 */
export function DispatchedDocumentModal({
  open,
  editing,
  initialReceivedId,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: DispatchedDocumentDetail | null;
  initialReceivedId?: string;
  onClose: () => void;
  onSaved: (saved: DispatchedDocumentDetail) => void;
}) {
  const toast = useToast();
  const canAttach = usePermission('document-dispatching.attach');

  const [source, setSource] = useState<Source>(
    editing ? (editing.receivedDocumentId != null ? 'received' : 'originated') : 'received',
  );
  const [receivedId, setReceivedId] = useState(editing?.receivedDocumentId ?? initialReceivedId ?? '');
  const [originId, setOriginId] = useState(editing && editing.receivedDocumentId == null ? editing.originatingOrgUnitId : '');
  const [subject, setSubject] = useState(editing?.subject ?? '');
  const [medium, setMedium] = useState(editing ? String(editing.medium) : '');
  const [recipientKind, setRecipientKind] = useState<CorrespondentKind>(
    editing?.recipientOrgUnitId != null ? 'internal' : 'external',
  );
  const [recipientUnitId, setRecipientUnitId] = useState(editing?.recipientOrgUnitId ?? '');
  const [recipientOffice, setRecipientOffice] = useState(editing?.recipientOffice ?? '');
  const [recipient, setRecipient] = useState<PersonNameParts>(EMPTY_NAME);
  const [mode, setMode] = useState(editing ? String(editing.mode) : '');
  const [dispatchedAt, setDispatchedAt] = useState(toDateTimeLocal(editing ? editing.dispatchedAt : new Date()));
  const [carrierReference, setCarrierReference] = useState(editing?.carrierReferenceNumber ?? '');
  const [remarks, setRemarks] = useState(editing?.remarks ?? '');
  const [files, setFiles] = useState<File[]>([]);

  const type = useDocumentTypeField(editing ? { id: editing.documentTypeId, name: editing.documentTypeName } : null);

  const [dispatchable, setDispatchable] = useState<DispatchableDocumentOption[]>([]);
  const [units, setUnits] = useState<RoutingUnitOption[]>([]);
  const [loadingFeeds, setLoadingFeeds] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      // The dispatchable feed carries `create` only — an edit never re-picks the source.
      editing ? Promise.resolve([]) : dispatchedDocumentsApi.dispatchable(controller.signal).catch(() => []),
      dispatchedDocumentsApi.units(controller.signal).catch(() => []),
    ])
      .then(([sources, unitRows]) => {
        if (controller.signal.aborted) return;
        setDispatchable(sources);
        setUnits(unitRows);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingFeeds(false);
      });

    return () => controller.abort();
  }, [editing]);

  const receivedOptions = useMemo(() => {
    // AN OPTION SAYS WHAT IT IS — the subject is the label, the tracking number the sublabel.
    const live = dispatchable.map((d) => ({
      value: d.id,
      label: d.subject,
      sublabel: `${d.trackingNumber} · from ${d.sentBy} · with ${d.holders.map((h) => h.name).join(', ')}`,
      keywords: `${d.trackingNumber} ${d.sentBy} ${d.holders.map((h) => h.name).join(' ')}`,
    }));

    return editing?.receivedDocumentId != null
      ? [
          {
            value: editing.receivedDocumentId,
            label: editing.receivedSubject ?? editing.receivedTrackingNumber ?? 'Received document',
            sublabel: editing.receivedTrackingNumber ?? undefined,
          },
        ]
      : live;
  }, [dispatchable, editing]);

  const unitOptions = useMemo(() => {
    const live = units.map((u) => ({ value: u.id, label: u.name, sublabel: u.code ?? undefined, keywords: u.code ?? undefined }));

    return editing && editing.receivedDocumentId == null && !units.some((u) => u.id === editing.originatingOrgUnitId)
      ? [{ value: editing.originatingOrgUnitId, label: editing.originatingOrgUnitName }, ...live]
      : live;
  }, [units, editing]);

  /** Picking a received document SEEDS what it already says — the reader may still change any of it. */
  function pickReceived(next: string) {
    setReceivedId(next);

    const picked = dispatchable.find((d) => d.id === next);

    // The unit it leaves from belongs to the document — a different one clears it, or takes its only holder.
    setOriginId(picked?.holders.length === 1 ? picked.holders[0].id : '');

    if (picked && !editing) {
      if (subject.trim() === '') setSubject(picked.subject);
      if (!type.answered) type.set(picked.documentTypeId);
    }
  }

  // The received document chosen, and the units it is with — the ones it may leave from.
  const pickedHolders = useMemo(() => dispatchable.find((d) => d.id === receivedId)?.holders ?? [], [dispatchable, receivedId]);
  const choosesHolder = source === 'received' && !editing && pickedHolders.length > 1;

  const sourceAnswered =
    source === 'received'
      ? receivedId !== '' && (!choosesHolder || pickedHolders.some((h) => h.id === originId))
      : originId !== '';

  // The unit it leaves from — a holder of the received document, or the unit that wrote it.
  const fromUnitId =
    source === 'received'
      ? editing?.receivedDocumentId != null
        ? editing.originatingOrgUnitId
        : pickedHolders.length === 1
          ? pickedHolders[0].id
          : originId
      : originId;

  // Offered as the From unit: every unit for a document a unit wrote; the holders for a received one.
  const fromOptions = source === 'received'
    ? pickedHolders.map((h) => ({ value: h.id, label: h.name, sublabel: h.code ?? undefined, keywords: h.code ?? undefined }))
    : unitOptions;

  const internal = recipientKind === 'internal';

  // Received by the Inbox — the addressee is history now.
  const recipientLocked = editing?.inboxReceivedAt != null;

  // A unit does not send to itself; the one it leaves from is not offered. The recorded one stays.
  const recipientOptions = useMemo(() => {
    const live = units
      .filter((u) => u.id !== fromUnitId)
      .map((u) => ({ value: u.id, label: u.name, sublabel: u.code ?? undefined, keywords: u.code ?? undefined }));

    return editing?.recipientOrgUnitId != null
      && editing.recipientOrgUnitName != null
      && !units.some((u) => u.id === editing.recipientOrgUnitId)
      ? [{ value: editing.recipientOrgUnitId, label: editing.recipientOrgUnitName }, ...live]
      : live;
  }, [units, fromUnitId, editing]);

  const valid =
    sourceAnswered
    && subject.trim().length > 0
    && type.answered
    && medium !== ''
    && (internal ? recipientUnitId !== '' && recipientUnitId !== fromUnitId : recipientOffice.trim().length > 0)
    && mode !== ''
    && dispatchedAt !== ''
    && (internal || !nameIncomplete(recipient))
    && !files.some(fileTooLarge);

  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const documentTypeId = await type.resolve();

      const body = {
        receivedDocumentId: source === 'received' ? receivedId : null,
        // A received document names its holder only when it has several — the server reads a lone one.
        originatingOrgUnitId: source === 'originated' ? originId : choosesHolder ? originId : null,
        subject: subject.trim(),
        documentTypeId,
        medium: Number(medium) as DocumentMedium,
        // ONE side of the addressee reaches the wire; the other is cleared, never sent stale.
        recipientOrgUnitId: internal ? recipientUnitId : null,
        recipientOffice: internal ? null : recipientOffice.trim(),
        recipientSalutation: internal ? null : recipient.salutation.trim() || null,
        recipientFirstName: internal ? null : recipient.firstName.trim() || null,
        recipientMiddleName: internal ? null : recipient.middleName.trim() || null,
        recipientLastName: internal ? null : recipient.lastName.trim() || null,
        recipientSuffix: internal ? null : recipient.suffix.trim() || null,
        mode: Number(mode) as DispatchMode,
        dispatchedAt: new Date(dispatchedAt).toISOString(),
        carrierReferenceNumber: carrierReference.trim() || null,
        remarks: remarks.trim() || null,
      };

      const saved = editing
        ? await dispatchedDocumentsApi.update(editing.id, body)
        : await dispatchedDocumentsApi.create(body);

      if (!editing && files.length > 0) {
        setProgress(0);
        const failed = await fileAll(dispatchedDocumentsApi, saved.id, files, setProgress);

        if (failed.length > 0) {
          toast.failure(
            `${saved.trackingNumber} was saved, but ${failed.map((f) => `${f.name} (${f.reason})`).join('; ')} could not be filed. File ${failed.length === 1 ? 'it' : 'them'} again from its Preview tab.`,
          );
        }
      }

      toast.success(editing ? `Saved ${saved.trackingNumber}.` : `Dispatched ${saved.trackingNumber}.`);
      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this dispatch.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID_PREFIX });
    } finally {
      setSaving(false);
      setProgress(null);
    }
  }

  function generate() {
    let from = fromUnitId;

    if (!editing) {
      if (dispatchable.length > 0 && Math.random() < 0.5) {
        const picked = dispatchable[Math.floor(Math.random() * dispatchable.length)];
        const holder = picked.holders[Math.floor(Math.random() * picked.holders.length)];
        setSource('received');
        setReceivedId(picked.id);
        setOriginId(holder.id);
        from = holder.id;
      } else if (units.length > 0) {
        const picked = units[Math.floor(Math.random() * units.length)];
        setSource('originated');
        setOriginId(picked.id);
        from = picked.id;
      }
    }

    const sample = sampleDispatchedDocument({ typeIds: type.liveIds, unitIds: units.map((u) => u.id), fromUnitId: from });

    setSubject(sample.subject);
    type.set(sample.documentTypeId);
    setMedium(String(sample.medium));
    if (!recipientLocked) setRecipientUnitId(sample.recipientOrgUnitId);
    setRecipientOffice(sample.recipientOffice);
    setRecipient(sample.recipient);
    setMode(String(sample.mode));
    setDispatchedAt(sample.dispatchedAt);
    setCarrierReference(sample.carrierReferenceNumber);
    setRemarks(sample.remarks);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${editing.trackingNumber}` : 'Dispatch document'}
      size="2xl"
      id={`${ID_PREFIX}-modal`}
      // Not until the catalogs it resolves foreign keys against have answered — it would fill none.
      autogenerate={{ onGenerate: generate, disabled: saving || type.loading || loadingFeeds }}
      confirm={{ id: `${ID_PREFIX}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID_PREFIX}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel={progress === null ? 'Submitting' : `Filing ${progress}%`}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <RadioGroup<Source>
          id={`${ID_PREFIX}-source`}
          label="What is being sent"
          required
          description={
            editing
              ? 'What a dispatch sends cannot change. Delete it and record it again if this is wrong.'
              : 'A document the organization received and is sending on, or one a unit wrote.'
          }
          value={source}
          onChange={setSource}
          options={[
            { value: 'received', label: 'A received document' },
            { value: 'originated', label: 'A document a unit wrote' },
          ]}
          columns={2}
          disabled={saving || editing !== null}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id={`${ID_PREFIX}-received-document-id`}
            label="Received document"
            required={source === 'received'}
            description={
              source === 'received'
                ? 'Only documents on hand with a unit are offered — one still in transit must be acknowledged first. It leaves from the unit holding it.'
                : 'Not asked: this dispatch is a document a unit wrote.'
            }
            value={source === 'received' ? receivedId : ''}
            onChange={pickReceived}
            options={receivedOptions}
            remote
            loading={loadingFeeds}
            disabled={saving || source !== 'received' || editing !== null}
            error={error?.fieldError('receivedDocumentId')}
          />

          <Select
            id={`${ID_PREFIX}-originating-org-unit-id`}
            label="From unit"
            required={source === 'originated' || choosesHolder}
            description={
              source === 'originated'
                ? 'The unit that wrote it and is sending it out.'
                : choosesHolder
                  ? 'It is with several units. Choose the one sending its copy out — the others keep theirs.'
                  : 'Not asked: a received document leaves from the unit holding it.'
            }
            value={source === 'originated' || choosesHolder ? originId : ''}
            onChange={setOriginId}
            options={fromOptions}
            remote
            loading={loadingFeeds}
            disabled={saving || (source !== 'originated' && !choosesHolder)}
            error={error?.fieldError('originatingOrgUnitId')}
          />

          <div className="sm:col-span-2">
            <TextField
              id={`${ID_PREFIX}-subject`}
              label="Subject"
              required
              description="What the document is about, as its subject line reads."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={300}
              disabled={saving}
              error={error?.fieldError('subject')}
            />
          </div>
        </div>

        <RadioGroup<CorrespondentKind>
          id={`${ID_PREFIX}-recipient-kind`}
          label="To"
          required
          description={
            recipientLocked
              ? 'The unit it was sent to has already received it, so where it went can no longer be changed.'
              : "A unit of this organization — it lands in that unit's Inbox — or an office outside it."
          }
          value={recipientKind}
          onChange={setRecipientKind}
          options={CORRESPONDENT_KIND_OPTIONS}
          columns={2}
          disabled={saving || recipientLocked}
        />

        {internal ? (
          <Select
            id={`${ID_PREFIX}-recipient-org-unit-id`}
            label="To (unit)"
            required
            description={
              recipientLocked
                ? "Received by this unit's Inbox, so it is fixed."
                : "The unit it is sent to. It is in transit until that unit's Inbox receives it. The unit it leaves from is not offered."
            }
            value={recipientUnitId}
            onChange={setRecipientUnitId}
            options={recipientOptions}
            remote
            loading={loadingFeeds}
            disabled={saving || recipientLocked}
            error={error?.fieldError('recipientOrgUnitId')}
          />
        ) : (
          <>
            <TextField
              id={`${ID_PREFIX}-recipient-office`}
              label="To (organization)"
              required
              description="The office, agency or company it went to — an organization, not a person. The person it is addressed to, if any, goes in the named addressee below."
              value={recipientOffice}
              onChange={(e) => setRecipientOffice(e.target.value)}
              maxLength={200}
              disabled={saving}
              error={error?.fieldError('recipientOffice')}
            />

            {/* Directly beneath the organization it is addressed to — an outside office only. */}
            <CorrespondentNameFields
              idPrefix={ID_PREFIX}
              role="addressee"
              field="recipient"
              value={recipient}
              onChange={setRecipient}
              editing={editing !== null && editing.recipientPersonId != null}
              error={error}
              disabled={saving}
            />
          </>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id={`${ID_PREFIX}-document-type-id`}
            label="Document type"
            required
            description={
              type.canCreate
                ? 'What kind of document it is. Type a new kind to add it to the catalog when this is saved.'
                : 'What kind of document it is, from the catalog under Routing › Settings.'
            }
            value={type.value}
            onChange={type.onChange}
            options={type.options}
            remote
            loading={type.loading}
            allowCustom={type.canCreate}
            onCreateOption={type.onCreateOption}
            disabled={saving}
            error={error?.fieldError('documentTypeId')}
          />

          <Select
            id={`${ID_PREFIX}-medium`}
            label="Sent as"
            required
            description="Hard copy is paper; soft copy went as a file."
            value={medium}
            onChange={setMedium}
            options={MEDIUM_OPTIONS}
            sort="none"
            searchable={false}
            disabled={saving}
            error={error?.fieldError('medium')}
          />

          <Select
            id={`${ID_PREFIX}-mode`}
            label="Sent by"
            required
            description="How it was carried — by hand, courier, mail or email."
            value={mode}
            onChange={setMode}
            options={MODE_OPTIONS}
            sort="none"
            searchable={false}
            disabled={saving}
            error={error?.fieldError('mode')}
          />

          <TextField
            id={`${ID_PREFIX}-dispatched-at`}
            label="Dispatched"
            required
            type="datetime-local"
            description="When it was sent. It cannot be in the future, nor — for a received document — before its last movement on the route."
            value={dispatchedAt}
            onChange={(e) => setDispatchedAt(e.target.value)}
            className="font-mono tabular-nums"
            disabled={saving}
            error={error?.fieldError('dispatchedAt')}
          />

          <div className="sm:col-span-2">
            <TextField
              id={`${ID_PREFIX}-carrier-reference-number`}
              label="Waybill / receipt no."
              description="Whatever proves it left — a courier's waybill, a registry receipt number. Optional."
              value={carrierReference}
              onChange={(e) => setCarrierReference(e.target.value)}
              maxLength={100}
              className="font-mono"
              disabled={saving}
              error={error?.fieldError('carrierReferenceNumber')}
            />
          </div>
        </div>

        {!editing && canAttach ? (
          <RoutingFilesInput id={`${ID_PREFIX}-files`} files={files} onChange={setFiles} disabled={saving} />
        ) : null}

        <Textarea
          id={`${ID_PREFIX}-remarks`}
          label="Remarks"
          description="A free note about what was sent — enclosures, copies furnished, anything the file should say."
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          disabled={saving}
          error={error?.fieldError('remarks')}
        />
      </div>
    </Modal>
  );
}
